/**
 * AnalyticsEventBuilder
 *
 * Pure-function factory that stamps every event with a UUID, timestamp,
 * session ID, user/entity context, and sync status.  No side-effects.
 */
import { Injectable } from '@angular/core';
import {
    AnalyticsEventBase,
    AnalyticsEventType,
    CartAbandonEvent,
    CartAddEvent,
    CartCheckoutClickEvent,
    CartQuantityChangeEvent,
    CartRemoveEvent,
    CreatePOOpenEvent,
    GeneratePOClickEvent,
    PageViewEvent,
    POFieldInteractionEvent,
    POItemRemoveEvent,
    POQuantityEditEvent,
    POResultEvent,
    PriceComparisonViewEvent,
    ProceedToBuyEvent,
    ProceedToBuyResultEvent,
    QuickAddCategoryViewEvent,
    QuickAddItemSelectEvent,
    SearchEvent,
    SearchItemSelectEvent,
    SearchResultViewEvent,
    SessionEvent,
    SupplierType,
    SyncStatus,
} from '../models/analytics.interfaces';

@Injectable()
export class AnalyticsEventBuilder {
    private sessionId = '';
    private userId = '';
    private userName = '';
    private userEmail = '';
    private entityId = '';
    private companyId = '';
    private companyName = '';

    // ───────────── Context Setup ──────────────

    setSessionId(id: string): void {
        this.sessionId = id;
    }

    setUserContext(ctx: {
        userId: string;
        userName: string;
        userEmail: string;
        entityId: string;
        companyId: string;
        companyName: string;
    }): void {
        this.userId = ctx.userId;
        this.userName = ctx.userName;
        this.userEmail = ctx.userEmail;
        this.entityId = ctx.entityId;
        this.companyId = ctx.companyId;
        this.companyName = ctx.companyName;
    }

    // ───────────── Base Stamp ──────────────

    private base(
        eventType: AnalyticsEventType,
        supplier: SupplierType | string
    ): AnalyticsEventBase {
        return {
            eventId: this.uuid(),
            eventType,
            timestamp: new Date().toISOString(),
            sessionId: this.sessionId,
            userId: this.userId,
            userName: this.userName,
            userEmail: this.userEmail,
            entityId: this.entityId,
            companyId: this.companyId,
            companyName: this.companyName,
            supplier,
            syncStatus: SyncStatus.Pending,
        };
    }

    // ───────────── Search Events ──────────────

    search(
        supplier: SupplierType | string,
        data: SearchEvent['data']
    ): SearchEvent {
        return {
            ...this.base(AnalyticsEventType.Search, supplier),
            eventType: AnalyticsEventType.Search,
            data,
        };
    }

    searchResultView(
        supplier: SupplierType | string,
        data: SearchResultViewEvent['data']
    ): SearchResultViewEvent {
        return {
            ...this.base(AnalyticsEventType.SearchResultView, supplier),
            eventType: AnalyticsEventType.SearchResultView,
            data,
        };
    }

    searchItemSelect(
        supplier: SupplierType | string,
        data: SearchItemSelectEvent['data']
    ): SearchItemSelectEvent {
        return {
            ...this.base(AnalyticsEventType.SearchItemSelect, supplier),
            eventType: AnalyticsEventType.SearchItemSelect,
            data,
        };
    }

    // ───────────── Cart Events ──────────────

    cartAdd(
        supplier: SupplierType | string,
        data: CartAddEvent['data']
    ): CartAddEvent {
        return {
            ...this.base(AnalyticsEventType.CartAdd, supplier),
            eventType: AnalyticsEventType.CartAdd,
            data,
        };
    }

    cartRemove(
        supplier: SupplierType | string,
        data: CartRemoveEvent['data']
    ): CartRemoveEvent {
        return {
            ...this.base(AnalyticsEventType.CartRemove, supplier),
            eventType: AnalyticsEventType.CartRemove,
            data,
        };
    }

    cartQuantityChange(
        supplier: SupplierType | string,
        data: CartQuantityChangeEvent['data']
    ): CartQuantityChangeEvent {
        return {
            ...this.base(AnalyticsEventType.CartQuantityChange, supplier),
            eventType: AnalyticsEventType.CartQuantityChange,
            data,
        };
    }

    cartAbandon(
        supplier: SupplierType | string,
        data: CartAbandonEvent['data']
    ): CartAbandonEvent {
        return {
            ...this.base(AnalyticsEventType.CartAbandon, supplier),
            eventType: AnalyticsEventType.CartAbandon,
            data,
        };
    }

    // ───────────── Checkout Events ──────────────

    cartCheckoutClick(
        supplier: SupplierType | string,
        data: CartCheckoutClickEvent['data']
    ): CartCheckoutClickEvent {
        return {
            ...this.base(AnalyticsEventType.CartCheckoutClick, supplier),
            eventType: AnalyticsEventType.CartCheckoutClick,
            data,
        };
    }

    proceedToBuy(
        supplier: SupplierType | string,
        data: ProceedToBuyEvent['data']
    ): ProceedToBuyEvent {
        return {
            ...this.base(AnalyticsEventType.ProceedToBuy, supplier),
            eventType: AnalyticsEventType.ProceedToBuy,
            data,
        };
    }

    proceedToBuyResult(
        supplier: SupplierType | string,
        success: boolean,
        data: ProceedToBuyResultEvent['data']
    ): ProceedToBuyResultEvent {
        const type = success
            ? AnalyticsEventType.ProceedToBuySuccess
            : AnalyticsEventType.ProceedToBuyFailure;
        return {
            ...this.base(type, supplier),
            eventType: type,
            data,
        };
    }

    // ───────────── Local Supplier PO Events ──────────────

    createPOOpen(data: CreatePOOpenEvent['data']): CreatePOOpenEvent {
        return {
            ...this.base(AnalyticsEventType.CreatePOOpen, SupplierType.Local),
            eventType: AnalyticsEventType.CreatePOOpen,
            data,
        };
    }

    poFieldInteraction(
        data: POFieldInteractionEvent['data']
    ): POFieldInteractionEvent {
        return {
            ...this.base(AnalyticsEventType.POFieldInteraction, SupplierType.Local),
            eventType: AnalyticsEventType.POFieldInteraction,
            data,
        };
    }

    poQuantityEdit(data: POQuantityEditEvent['data']): POQuantityEditEvent {
        return {
            ...this.base(AnalyticsEventType.POQuantityEdit, SupplierType.Local),
            eventType: AnalyticsEventType.POQuantityEdit,
            data,
        };
    }

    poItemRemove(data: POItemRemoveEvent['data']): POItemRemoveEvent {
        return {
            ...this.base(AnalyticsEventType.POItemRemove, SupplierType.Local),
            eventType: AnalyticsEventType.POItemRemove,
            data,
        };
    }

    generatePOClick(data: GeneratePOClickEvent['data']): GeneratePOClickEvent {
        return {
            ...this.base(AnalyticsEventType.GeneratePOClick, SupplierType.Local),
            eventType: AnalyticsEventType.GeneratePOClick,
            data,
        };
    }

    poResult(success: boolean, data: POResultEvent['data']): POResultEvent {
        const type = success
            ? AnalyticsEventType.POSuccess
            : AnalyticsEventType.POFailure;
        return {
            ...this.base(type, SupplierType.Local),
            eventType: type,
            data,
        };
    }

    // ───────────── Session & Page Events ──────────────

    sessionStart(data: SessionEvent['data']): SessionEvent {
        return {
            ...this.base(AnalyticsEventType.SessionStart, '' as any),
            eventType: AnalyticsEventType.SessionStart,
            data,
        };
    }

    sessionEnd(data: SessionEvent['data']): SessionEvent {
        return {
            ...this.base(AnalyticsEventType.SessionEnd, '' as any),
            eventType: AnalyticsEventType.SessionEnd,
            data,
        };
    }

    pageView(data: PageViewEvent['data']): PageViewEvent {
        return {
            ...this.base(AnalyticsEventType.PageView, '' as any),
            eventType: AnalyticsEventType.PageView,
            data,
        };
    }

    // ───────────── Quick-Add & Comparison ──────────────

    quickAddCategoryView(
        data: QuickAddCategoryViewEvent['data']
    ): QuickAddCategoryViewEvent {
        return {
            ...this.base(AnalyticsEventType.QuickAddCategoryView, '' as any),
            eventType: AnalyticsEventType.QuickAddCategoryView,
            data,
        };
    }

    quickAddItemSelect(
        data: QuickAddItemSelectEvent['data']
    ): QuickAddItemSelectEvent {
        return {
            ...this.base(AnalyticsEventType.QuickAddItemSelect, '' as any),
            eventType: AnalyticsEventType.QuickAddItemSelect,
            data,
        };
    }

    priceComparisonView(
        supplier: SupplierType | string,
        data: PriceComparisonViewEvent['data']
    ): PriceComparisonViewEvent {
        return {
            ...this.base(AnalyticsEventType.PriceComparisonView, supplier),
            eventType: AnalyticsEventType.PriceComparisonView,
            data,
        };
    }

    // ───────────── Utility ──────────────

    /**
     * Lightweight UUID v4 (no crypto dependency required).
     * Uses Math.random(); collision risk is negligible for analytics.
     */
    private uuid(): string {
        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(
            /[xy]/g,
            (c) => {
                const r = (Math.random() * 16) | 0;
                const v = c === 'x' ? r : (r & 0x3) | 0x8;
                return v.toString(16);
            }
        );
    }
}
